"use client";

import { Activity, Brain, Compass, Flame, ChartLine as LineChart, Radar } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function StockColors() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const capabilities = [{
    number: "01",
    title: "Precision Material Selection",
    description: "Expert guidance on choosing the optimal substrate for your specific environmental and regulatory requirements.",
    Icon: LineChart
  }, {
    number: "02",
    title: "Industrial Grade Durability",
    description: "Engineered materials designed to withstand extreme temperatures, chemical exposure, and harsh industrial conditions.",
    Icon: Brain
  }, {
    number: "03",
    title: "Regulatory Compliance Standards",
    description: "Ensuring all labeling and identification solutions meet UL, NEMA, and industry-specific safety standards.",
    Icon: Radar
  }, {
    number: "04",
    title: "Custom Fabrication Precision",
    description: "Advanced CNC and laser cutting capabilities to match your exact panel dimensions and mounting specifications.",
    Icon: Compass
  }, {
    number: "05",
    title: "High-Contrast Legibility",
    description: "Optimized engraving and color-fill techniques for maximum readability in low-light or high-vibration environments.",
    Icon: Activity
  }, {
    number: "06",
    title: "Rapid Prototyping & Delivery",
    description: "Streamlined production workflows to ensure your critical identification components arrive on schedule.",
    Icon: Flame
  }];
  return <section data-section-id="4553" id="stock-colors" className="bg-background text-foreground py-20 md:py-28 px-4 sm:px-6 lg:px-10">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 mb-16 md:mb-20 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-5">
            <p className="font-mono text-xs tracking-widest uppercase text-[color:var(--chart-1)] mb-6">
              03 / Materials
            </p>
            <h2 className="font-default font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-foreground">
              Industrial identification, perfected.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pl-8 flex items-end">
            <p className="font-default text-base md:text-lg leading-relaxed text-muted-foreground/90 max-w-2xl">
              Our material expertise combines technical precision with manufacturing excellence, transforming raw stock into durable, compliant identification solutions. Each material grade operates as a reliable foundation for your critical infrastructure labeling.
            </p>
          </div>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border/60">
          {capabilities.map((cap, i) => {
          const Icon = cap.Icon;
          return <div data-index={i} key={cap.number} className="group relative bg-background border border-transparent hover:border-[color:var(--chart-1)]/40 transition-all duration-500 ease-out p-8 sm:p-10 flex flex-col" style={{
            aspectRatio: "4 / 5",
            minHeight: "420px"
          }}>
                {}
                <span className="font-mono text-xs tracking-widest text-[color:var(--chart-1)]">
                  {cap.number}
                </span>

                {}
                <div className="relative flex items-center justify-start mt-10 mb-10">
                  <div aria-hidden="true" className="absolute -inset-6 rounded-lg bg-[color:var(--chart-1)]/15 blur-2xl opacity-60 group-hover:opacity-100 transition-opacity duration-500" />
                  <Icon strokeWidth={1} className="relative size-8 text-foreground/90 group-hover:text-[color:var(--chart-1)] transition-colors duration-500" />
                </div>

                {}
                <h3 className="font-default font-normal text-2xl leading-snug text-foreground mb-4">
                  {cap.title}
                </h3>

                {}
                <p className="font-default text-[15px] leading-relaxed text-muted-foreground/80 mb-8">
                  {cap.description}
                </p>

                {}
                
              </div>;
        })}
        </div>

        {}
        <div className="mt-16 md:mt-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pt-10 border-t border-border">
          <p className="font-default text-sm text-muted-foreground/80 max-w-xl">
            Optimize your facility identification with our professional-grade material solutions.
          </p>
          <Button asChild size="lg" className="bg-[color:var(--chart-1)] text-[color:var(--primary-foreground)] hover:bg-[color:var(--chart-1)]/90 font-mono text-xs tracking-widest uppercase">
            <Link to="/ContactPage">Request Quote</Link>
          </Button>
        </div>
      </div>
    </section>;
}